import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/api-client";
import type { components } from "@/types/api";
import type {
  AppNotification,
  NotificationWire,
} from "@/features/notification/types";

type NotificationPage =
  components["schemas"]["PageResponseNotificationResponse"];
type UnreadCount = components["schemas"]["UnreadCountResponse"];

function toNotification(wire: NotificationWire): AppNotification {
  return {
    id: wire.id!,
    type: wire.type!,
    entityType: wire.entityType!,
    entityId: wire.entityId!,
    title: wire.title!,
    message: wire.message ?? null,
    isRead: wire.isRead ?? false,
    readAt: wire.readAt ?? null,
    createdAt: wire.createdAt!,
  };
}

export const notificationKeys = {
  all: ["notifications"] as const,
  list: () => [...notificationKeys.all, "list"] as const,
  unreadCount: () => [...notificationKeys.all, "unread-count"] as const,
};

/**
 * Unread count for the bell badge. Polled every 30s (no websockets in this
 * stack) and refetched on window focus so "your turn" surfaces promptly.
 */
export function useUnreadCount() {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: async () => {
      const res = await apiRequest<UnreadCount>("/notifications/unread-count");
      return res.count ?? 0;
    },
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
}

/** Recent notifications (newest first). Enabled only when the panel is open. */
export function useNotifications(enabled = true) {
  return useQuery({
    queryKey: notificationKeys.list(),
    queryFn: async () => {
      const page = await apiRequest<NotificationPage>(
        "/notifications?page=0&size=20",
      );
      return (page.items ?? []).map(toNotification);
    },
    enabled,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiRequest<void>(`/notifications/${id}/read`, { method: "POST" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiRequest<void>("/notifications/read-all", { method: "POST" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}
